import React, { useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { connect, useStore } from 'react-redux';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import type Luminary from '@lumen5/luminary';

import { areEqualShallow, validatePropsInDev } from './comparison-utils';
import { LuminaryContext } from './luminary-context-provider';
import type { Connect, Options } from './luminary-redux-connect-types';

import type { RootState } from 'js/src/libs/RootState';

/**
 * luminaryReduxConnect attempts to be the equivalent of the react-redux `connect` method.  It wires up a component
 * with a `mapLuminaryStateToProps` method which allows for encapsulated luminary data reads assigned as props
 * to the component.  It specifically simplifies the common case where
 * a component should be wrapped by the LuminaryContextConsumer and ReduxContextConsumer.
 *
 * It binds luminary operations (see luminary-operations.ts) to the luminary instance and redux dispatch
 * method, making it simple to call from a component.
 *
 * Sample code:
 * ```
 *  import {sampleLuminaryOperation, bindLuminaryOperations, luminaryReduxConnect} from 'js/src/luminary';
 *
 *  const mapLuminaryStateToProps = (luminary: Luminary, ownProps: OwnProps, cacheRef: CacheRef, getState: GetRootState) => {
 *      return {
 *          aspectRatio: luminary.aspectRatio,
 *      };
 *  };
 *
 *  const mapLuminaryOperationsToProps = (dispatch: AppDispatch, getState: GetRootState, luminary: Luminary) => {
 *      return bindLuminaryOperations({
 *          sampleLuminaryOperation,
 *      }, dispatch, getState, luminary);
 *  };
 *
 *  const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
 *      return {
 *          activeTab: getCurrentEditorTab(state),
 *      };
 *  };
 *
 *  const mapDispatchToProps = (dispatch: AppDispatch) => {
 *      return bindActionCreators({
 *          showModal,
 *      },
 *      dispatch);
 *  };
 *
 *  const SceneGroupContainerConnector = luminaryReduxConnect(
 *      mapStateToProps,
 *      mapDispatchToProps,
 *      mapLuminaryStateToProps,
 *      mapLuminaryOperationsToProps,
 *      {renderOnlyIfScenePresent: (ownProps: OwnProps) => ownProps.slideId}
 *  )(PresentationComponent);
 *  ```
 *
 * @deprecated Use `useLuminarySelector` and `useLuminaryOperations` hooks instead.
 * See the luminary-hooks-refactor skill for migration guidance.
 */
export const luminaryReduxConnect: Connect = (
    mapStateToProps = undefined,
    mapDispatchToProps = undefined,
    mapLuminaryStateToProps = undefined,
    mapLuminaryOperationsToProps = undefined,
    options = {},
) => {
    return (Component) => {
        if (Component === undefined) {
            throw Error('You are passing in an undefined Component to luminaryReduxConnect');
        }

        const eventSubscriptionList: any[] = options.eventsToSubscribeTo ?? ['stateChanged'];
        const MemoizedLuminaryContextConsumer = React.memo((props: any) => {
            const luminary: Luminary | null = useContext(LuminaryContext);

            // fetch the redux store via hook to pass into mapLuminaryOperationsToProps
            const store = useStore<RootState>();

            // setup some default variables for state tracking
            const unmounted = useRef(false);
            const batchUpdateId = useRef<number | null>(null);

            // cache operations props, the dispatch methods should not change references each render
            // however, on scene design editor, the luminary instance is changed when switching tab in which case we need to regenerate operation props.
            const operationProps = useRef<any>(undefined);
            const lastLuminary = useRef<Luminary | null>(null);

            // cache the last stateProps that we've passed in to prevent unnecessary renders when luminary state changes
            const lastStateProps = useRef<any>(undefined);

            // this is a "cache" object for storing values in `cacheRef`
            const cache = useRef<Record<any, any>>({});

            // extract out any "forwardedRef" property from the props list (it's a "special" property)
            const { forwardedRef, ...otherProps } = props;

            // store propsWithoutRef as a ref so we can read it in doUpdate
            const propsWithoutRef = useRef<any>(undefined);
            propsWithoutRef.current = otherProps;

            // we're using this "forceUpdate" hack so when luminary emits a `stateChanged` event
            // we can force the component to re-render if necessary
            // more info: https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
            const [, forceUpdate] = useReducer((x) => x + 1, 0);

            /**
             * It's STRONGLY recommended to only fetch & pass-through primitives from Luminary, as that
             * works with PureComponents (given that Luminary doesn't support immutable objects).  When that's not possible,
             * the `cacheRef` option in mapLuminaryStateToProps allows for caching references to non-primitives.
             *
             * For example:
             * const mapLuminaryStateToProps= (luminary: Luminary, ownProps: OwnProps, cacheRef: CacheRef, getState: GetRootState) => {
             *     const derivedArray = luminary.scenes.forEach(...);
             *     return {
             *         value: dedeupeNonPrimitive('derived array', derivedArray);
             *     }
             * }
             *
             * NOTE: Because we're doing an `isEqual` (deep) equality check, if the value passed in is too complex there
             *       will be pretty significant performance impacts.
             * @param key – unique key for this property for this mapLuminaryStateToProps method
             * @param value - value to dedupe
             * @param {function} [transformer] - An optional function to transform the value argument into some other format
             * For example, converting to an Immutable object).
             * The final transformed version of the value is the data that will be returned by the cache (into the props).
             * The default behaviour is no transformation.
             * @returns {*}
             */
            const cacheRef = (key: any, value: any, transformer = (value: any) => value) => {
                if (!cache.current[key] || !isEqual(cache.current[key].value, value)) {
                    cache.current[key] = {
                        value,
                        transformedValue: transformer(value),
                    };
                }

                return cache.current[key].transformedValue;
            };

            /**
             * Track unmount.
             */
            useEffect(() => {
                return () => {
                    unmounted.current = true;
                };
            }, []);

            /**
             * We need to subscribe to Luminary events if there is any luminary-derived state (ie.
             * `mapLuminaryStateToProps). We want to do this in the same order as the components are created so we
             * can't use `useEffect`/`useLayoutEffect` as React processes those hooks for children before their parents.
             * That causes the event subscriptions to go out of the order we expect (top-down).
             * Instead, we use `useMemo()` to ensure we're only subscribing to events exactly once, right away. And then
             * we use `useEffect()` to unsubscribe when the component is being destroyed.
             */
            const cleanupSubscriptions = useMemo(() => {
                // subscribing to events is only necessary for mapLuminaryStateToProps, so if it's undefined, don't bother!
                if (!mapLuminaryStateToProps) {
                    return;
                }

                // define the doUpdate method in the memo closure
                const doUpdate = () => {
                    /**
                     * This method should be called anytime the Luminary state updates.
                     * This will compare to see if the luminary state extracted in mapLuminaryStateToProps has changed.
                     * If so, it triggers a forceUpdate.
                     */
                    batchUpdateId.current = null;
                    if (unmounted.current) {
                        return;
                    }

                    if (!shouldRender(options, luminary, propsWithoutRef.current)) {
                        return;
                    }
                    const stateProps = mapLuminaryStateToProps(
                        luminary,
                        propsWithoutRef.current,
                        cacheRef,
                        store.getState,
                    );
///////////////////////////////
//////////////////////////////////////////////////////////
///////////////////////////////////////////
//////////////////////////////////////
////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////
//////////////////////////
/////////////////////
//////////////////////////////
                    const areEqual = areEqualShallow(stateProps, lastStateProps.current);
                    if (!areEqual) {
                        forceUpdate();
                    }
                };

                const unsubscribeStore = store.subscribe(() => {
                    /**
                     * The callback is called synchronously for every store update. However, we
                     * can save a decent amount of work if we batch processing store changes.
                     */
                    if (mapLuminaryStateToProps && mapLuminaryStateToProps.length >= 4) {
                        if (batchUpdateId.current === null) {
                            batchUpdateId.current = requestAnimationFrame(doUpdate);
                        }
                    }
                });

                const eventSelectors = eventSubscriptionList.map((eventOrEventSelector) => {
                    if (typeof eventOrEventSelector === 'string') {
                        return {
                            eventType: eventOrEventSelector,
                            listener: doUpdate,
                        };
                    }
                    return {
                        eventType: eventOrEventSelector.eventType,
                        listener: (eventData: unknown) => {
                            if (
                                eventOrEventSelector.triggerIf(propsWithoutRef.current, eventData)
                            ) {
                                doUpdate();
                            }
                        },
                    };
                });

                // anytime events in the "eventSubscriptionList" are fired, trigger a doUpdate() call to
                // cause the properties to be re-read
                if (luminary) {
                    eventSelectors.forEach((eventSelector) => {
                        luminary.on(eventSelector.eventType, eventSelector.listener);
                    });
                }

                return function cleanup() {
                    unsubscribeStore();

                    // unsubscribe all events
                    if (luminary) {
                        eventSelectors.forEach((eventSelector) => {
                            luminary.off(eventSelector.eventType, eventSelector.listener);
                        });
                    }

                    if (batchUpdateId.current) {
                        cancelAnimationFrame(batchUpdateId.current);
                        batchUpdateId.current = null;
                    }
                };
            }, [luminary, store]);

            useEffect(() => {
                return cleanupSubscriptions;
            }, [cleanupSubscriptions]);

            // check if we should render (if not, return null)
            if (!shouldRender(options, luminary, propsWithoutRef.current)) {
                return null;
            }

            if (luminary && lastLuminary.current !== luminary) {
                operationProps.current = mapLuminaryOperationsToProps
                    ? mapLuminaryOperationsToProps(store.dispatch, store.getState, luminary, props)
                    : {};
                lastLuminary.current = luminary;
            }

            // render the component with all the props passed through!
            const stateProps = mapLuminaryStateToProps
                ? mapLuminaryStateToProps(
                      luminary,
                      propsWithoutRef.current,
                      cacheRef,
                      store.getState,
                  )
                : {};
            lastStateProps.current = stateProps; // cache the stateProps for `doUpdate()` to compare against

            return (
                <Component
                    ref={forwardedRef}
                    {...propsWithoutRef.current}
                    {...stateProps}
                    {...operationProps.current}
                />
            );
        }) as any;

        // set the display name (inspired by react-redux: https://github.com/reduxjs/react-redux/blob/a2853bcf2e04e073ba2ac13e3bb48829edcc2b60/src/components/connect.tsx#L491)
        MemoizedLuminaryContextConsumer.displayName = `LuminaryReduxConnect(${(Component as any).displayName || Component.name || (Component as any).type?.name || 'Component'})`;

        let luminaryComponent = MemoizedLuminaryContextConsumer;

        // If using forwardRef, get it and attach manually
        if (options.forwardRef) {
            luminaryComponent = React.forwardRef(
                function LuminaryReduxConnectForwardRef(props, ref) {
                    return <MemoizedLuminaryContextConsumer {...props} forwardedRef={ref} />;
                },
            );
        }

        // return the method wrapped in `connect`
        return connect(
            mapStateToProps,
            mapDispatchToProps,
            undefined, // we don't support the mergeProps param
            options, // pass through options so forwardedRef (if present) is processed
        )(luminaryComponent) as any;
    };
};

const uuidRe = new RegExp(
    /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/,
);
function isUuid(test: any) {
    return uuidRe.test(test);
}

/**
 * simple method to determine if the LuminaryContextConsumer component should render.  This is dependent on the
 * `options` passed into `luminaryReduxConnect`
 * If no options are specified, always returns true.
 * Otherwise it goes through all params and returns true if all of them evaluate to true.
 */
export const shouldRender = <State, TStateProps, TOwnProps>(
    options: Options<State, TStateProps, TOwnProps>,
    luminary: Luminary | null,
    propsWithoutRef: any,
): luminary is Luminary => {
    // if luminary is null or has been destroyed, return false
    if (luminary === null || luminary.destroyed) {
        return false;
    }

    // if none of the options are specified, return true
    if (
        options.renderOnlyIfScenePresent === undefined &&
        options.renderOnlyIfAssetPresent === undefined &&
        options.renderOnlyIfChildPresent === undefined &&
        options.renderOnlyIfSceneGroupPresent === undefined &&
        options.renderOnlyIfPathPresent === undefined
    ) {
        return true;
    }

    let scene;
    if (typeof options.renderOnlyIfSceneGroupPresent === 'function') {
        const sceneGroupId = options.renderOnlyIfSceneGroupPresent(propsWithoutRef);
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return false;
        }
        scene = sceneGroup.scenes[0];
    }
    // renderOnlyIfScenePresent has several modes.  Evaluate them in order.
    // boolean -> if true, only render if 1+ slides present
    if (options.renderOnlyIfScenePresent === true && luminary.scenes.length === 0) {
        return false;
    }
    // other options specify a specific slide
    if (options.renderOnlyIfScenePresent !== undefined) {
        let sceneValue = options.renderOnlyIfScenePresent;
        if (typeof sceneValue === 'function') {
            sceneValue = sceneValue(propsWithoutRef);
        }
        // string -> is the slideId, only render if slide exists
        if (typeof sceneValue === 'string') {
            scene = luminary.getSceneById(sceneValue);
        }
        // number -> is the slide index, only render if slide exists
        else if (typeof sceneValue === 'number') {
            scene = luminary.scenes.length >= sceneValue ? luminary.scenes[sceneValue] : undefined;
        }
        // boolean -> is the first scene, only render if slide exists
        else if (typeof sceneValue === 'boolean') {
            scene = luminary.scenes.length >= 1 ? luminary.scenes[0] : undefined;
        } else {
            throw Error(
                `invalid renderOnlyIfScenePresent value: ${options.renderOnlyIfScenePresent as string}`,
            );
        }
    } else {
        // assume first scene since scene not specified
        scene = luminary.scenes.length >= 1 ? luminary.scenes[0] : undefined;
    }
    if (scene === undefined || scene.destroyed) {
        return false;
    }

    // error if both `renderOnlyIfAssetPresent` and `renderOnlyIfChildPresent` are specified
    if (options.renderOnlyIfAssetPresent && options.renderOnlyIfChildPresent) {
        throw Error(
            'renderOnlyIfAssetPresent and renderOnlyIfChildPresent cannot both be specified',
        );
    }

    // If a specific child is specified, see if it exists
    if (options.renderOnlyIfChildPresent) {
        // get the child name from the options.renderOnlyIfChildPresent param
        // string -> is the slideId, only render if slide exists
        let childNameOrId;
        if (typeof options.renderOnlyIfChildPresent === 'string') {
            childNameOrId = options.renderOnlyIfChildPresent;
        }
        // function -> returns the slideId, only render if slide exists
        else if (typeof options.renderOnlyIfChildPresent === 'function') {
            childNameOrId = options.renderOnlyIfChildPresent(propsWithoutRef);
        } else if (options.renderOnlyIfSceneGroupPresent === undefined) {
            childNameOrId = undefined;
        } else {
            throw Error(
                `invalid renderOnlyIfChildPresent value: ${options.renderOnlyIfChildPresent as string}`,
            );
        }

        if (childNameOrId === undefined || childNameOrId === null) {
            return true;
        }

        // If childOnSubscene is given then we need to limit to a specific subscene
        let subsceneIndex;
        if (typeof options.childOnSubscene === 'number') {
            subsceneIndex = options.childOnSubscene;
        } else if (typeof options.childOnSubscene === 'function') {
            subsceneIndex = options.childOnSubscene(propsWithoutRef);
        }

        let asset;
        if (Number.isInteger(subsceneIndex)) {
            const subscene = scene.subscenes[subsceneIndex];
            if (subscene === undefined) {
                // desired subscene does not exist
                return false;
            }

            if (isUuid(childNameOrId)) {
                asset = subscene.getAssetById(childNameOrId);
            } else {
                asset = subscene.getAssetByName(childNameOrId);
            }
        } else {
            if (isUuid(childNameOrId)) {
                asset = scene.getAssetById(childNameOrId);
            } else {
                asset = scene.getAssetByName(childNameOrId);
            }
        }

        if (!asset || asset.destroyed) {
            return false;
        }

        if (options.renderOnlyIfPathPresent) {
            if (typeof options.renderOnlyIfPathPresent === 'function') {
                if (
                    get(asset, options.renderOnlyIfPathPresent(propsWithoutRef) as string) ===
                    undefined
                ) {
                    return false;
                }
            } else {
                throw Error(
                    `Invalid renderOnlyIfPathPresent value: ${options.renderOnlyIfPathPresent}`,
                );
            }
        }

        return true;
    }

    // If a specific asset is specified, see if it exists
    if (options.renderOnlyIfAssetPresent) {
        let assetId;
        if (typeof options.renderOnlyIfAssetPresent === 'string') {
            assetId = options.renderOnlyIfAssetPresent;
        } else if (typeof options.renderOnlyIfAssetPresent === 'function') {
            assetId = options.renderOnlyIfAssetPresent(propsWithoutRef);
        } else {
            throw Error(
                `invalid renderOnlyIfAssetPresent value: ${options.renderOnlyIfAssetPresent as string}`,
            );
        }

        if (!assetId) {
            // user error, an asset id couldn't be retrieved!
            // eslint-disable-next-line no-console
            console.warn('renderOnlyIfAssetPresent specified but assetId is not defined.');
            return false;
        }
        const sceneGroup = scene.sceneGroup;
        if (!sceneGroup) {
            return false;
        }
        const asset = scene.getAssetById(assetId) || sceneGroup.captions?.getAssetById(assetId);
        return !!asset && !asset.destroyed;
    }

    return true;
};
